import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, Camera } from "@phosphor-icons/react";

/**
 * Client-side image compression → faster OCR uploads.
 * Downscales to max 2400px longest-edge, JPEG 0.95 quality.
 * 2400px is the minimum needed for the 車検証 2D-barcode (QRコード) to remain
 * sharp enough for backend zbar decoder (empirically verified). Do NOT lower.
 */
async function compressImage(file, maxEdge = 2400, quality = 0.95) {
    const reader = new FileReader();
    const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
    const img = new Image();
    await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = dataUrl;
    });
    const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
    // If the source is already small enough AND already a JPEG, return as-is
    // (upscaling blurs the QR pattern).
    if (scale >= 1 && file.type === "image/jpeg") {
        return dataUrl;
    }
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    // Use high-quality smoothing (LANCZOS-like downscaling)
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
}

/**
 * OCR Dialog — uploads 車検証 image → Tesseract extracts fields →
 * onExtracted({...extracted}) is fired so the parent can open the
 * 新規登録フォーム with all fields pre-filled.
 */
export default function OcrDialog({ open, onClose, onExtracted }) {
    const [dataUrl, setDataUrl] = useState("");
    const [sourceSize, setSourceSize] = useState(0);
    const [compressedSize, setCompressedSize] = useState(0);
    const [loading, setLoading] = useState(false);

    if (!open) return null;

    const handleFile = async (f) => {
        if (!f) return;
        setSourceSize(f.size);
        try {
            const compressed = await compressImage(f);
            setDataUrl(compressed);
            setCompressedSize(Math.round((compressed.length * 3) / 4));
        } catch {
            const reader = new FileReader();
            reader.onload = (ev) => setDataUrl(ev.target.result);
            reader.readAsDataURL(f);
        }
    };

    const submit = async () => {
        if (!dataUrl) {
            toast.error("画像を選択してください");
            return;
        }
        setLoading(true);
        const started = performance.now();
        try {
            const { data } = await api.post("/ocr-free/vehicle-doc", {
                image_base64: dataUrl,
                save_draft: false,
            }, { timeout: 90000 });
            const elapsed = Math.round(performance.now() - started);
            const extracted = data.extracted || {};
            const confidence = data.confidence || {};
            const sources = data.sources || {};
            const qrBlocks = data.qr_blocks_found || 0;
            const fieldKeys = Object.keys(extracted).filter(k => k !== "_ocr_text");
            const highCount = fieldKeys.filter(k => confidence[k] === "high").length;
            const reviewCount = fieldKeys.length - highCount;
            const reviewSuffix = reviewCount > 0 ? `（うち${reviewCount}項目は要確認）` : "";
            // Toast variants depending on QR coverage
            if (qrBlocks >= 5) {
                toast.success(`QRコード5ブロック読取成功 (${(elapsed / 1000).toFixed(1)}秒) — ${fieldKeys.length}項目を自動入力${reviewSuffix}`);
                // 電子車検証記録事項 doesn't print all specs (e.g. 車両重量/寸法/排気量) —
                // those live in the IC tag or on the main 自動車検査証. Inform user.
                setTimeout(() => toast.info("電子車検証記録事項は一部項目（車名/燃料/重量/寸法など）が印字されていないため、それらは手入力してください。", { duration: 7000 }), 500);
            } else if (qrBlocks >= 1) {
                toast.warning(`QRコード${qrBlocks}ブロックのみ読取 (${(elapsed / 1000).toFixed(1)}秒) — ${fieldKeys.length}項目入力。画質が不足しています。不足項目は手入力してください`);
            } else if (fieldKeys.length > 0) {
                toast.warning(`OCRフォールバックで${fieldKeys.length}項目抽出 (${(elapsed / 1000).toFixed(1)}秒) — QR未検出のため精度が低下します`);
            } else {
                toast.error(`読取に失敗しました。もっと鮮明な写真で再試行してください（正面・明るい場所推奨）`);
            }
            setDataUrl("");
            setSourceSize(0);
            setCompressedSize(0);
            if (onExtracted) onExtracted(extracted, dataUrl, confidence, sources);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "OCR失敗");
        } finally {
            setLoading(false);
        }
    };

    const close = () => {
        setDataUrl("");
        setSourceSize(0);
        setCompressedSize(0);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ocr-dialog">
            <div className="prc-card p-6 max-w-xl w-full max-h-[90vh] overflow-auto">
                <p className="prc-label-overline mb-1">OCR 車検証自動読取</p>
                <h2 className="text-xl font-bold mb-2">車検証の写真をアップロード</h2>
                <p className="text-sm text-[var(--prc-muted)] mb-5">
                    無料ローカルOCR（Tesseract 5 / jpn）で車検証を読み取り、<span className="text-[var(--prc-green)] font-semibold">新規登録フォームの各項目に自動入力</span>します。外部AI課金なし。入力後に管理者が内容を確認してから登録してください。
                </p>

                {!dataUrl && (
                    <label className="block border-2 border-dashed border-[var(--prc-border-strong)] rounded-sm p-10 text-center cursor-pointer hover:bg-[var(--prc-bg)] transition" data-testid="ocr-dropzone">
                        <UploadSimple size={24} className="mx-auto text-[var(--prc-muted)]" />
                        <p className="mt-3 text-sm font-semibold">画像を選択または撮影</p>
                        <p className="mt-1 text-xs text-[var(--prc-muted)]">JPEG / PNG / WEBP</p>
                        <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => handleFile(e.target.files?.[0])}
                            className="hidden"
                            data-testid="ocr-file-input"
                        />
                    </label>
                )}

                {dataUrl && (
                    <div>
                        <img src={dataUrl} alt="車検証" className="w-full max-h-72 object-contain border border-[var(--prc-border)] rounded-sm" data-testid="ocr-preview" />
                        {sourceSize > 0 && (
                            <p className="mt-2 text-[10px] font-mono text-[var(--prc-muted)] text-right" data-testid="ocr-compress-info">
                                画像圧縮: {(sourceSize / 1024).toFixed(0)}KB → {(compressedSize / 1024).toFixed(0)}KB
                            </p>
                        )}
                        {loading && (
                            <div className="mt-3 border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm px-3 py-2.5" data-testid="ocr-loading-info">
                                <p className="text-xs text-[var(--prc-green)] font-mono uppercase tracking-wider animate-pulse">
                                    ローカルOCR解析中... QR + Tesseract + EasyOCR を並列実行中（10〜20秒・無料）
                                </p>
                            </div>
                        )}
                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={() => { setDataUrl(""); setSourceSize(0); setCompressedSize(0); }} disabled={loading} className="prc-btn-ghost" data-testid="ocr-reselect-btn">選択し直す</button>
                            <button onClick={submit} disabled={loading} className="prc-btn-primary inline-flex items-center gap-2" data-testid="ocr-submit-btn">
                                <Camera size={16} /> {loading ? "解析中…" : "OCR解析を実行"}
                            </button>
                        </div>
                    </div>
                )}

                {!dataUrl && (
                    <div className="mt-4 flex justify-end">
                        <button onClick={close} className="prc-btn-ghost" data-testid="ocr-cancel-btn">キャンセル</button>
                    </div>
                )}
            </div>
        </div>
    );
}
